import { useQueryClient } from "@tanstack/react-query";
import { Lock, LockOpen } from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { PRIVATE_PHONES_KEY as KEY } from "./usePrivatePhones";

/** "Solo admins": hides this WhatsApp chat from the setters (or shows it again). */
export const PrivateChatToggle = ({
  phone,
  name,
  isPrivate,
}: {
  phone: string;
  name?: string | null;
  isPrivate: boolean;
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      const { error } = await getSupabaseClient().rpc(
        "gula_set_private_phone",
        {
          p_phone: phone,
          p_private: !isPrivate,
          p_label: name ?? null,
        },
      );
      if (error) throw new Error(error.message);
      await queryClient.invalidateQueries({ queryKey: KEY });
      notify(
        isPrivate
          ? "Los setters vuelven a ver este chat"
          : "Chat solo para administradores: los setters ya no lo ven",
        { type: "success" },
      );
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "No se ha podido cambiar",
        {
          type: "error",
        },
      );
    } finally {
      setBusy(false);
    }
  };

  const Icon = isPrivate ? Lock : LockOpen;
  return (
    <button
      type="button"
      disabled={busy}
      onClick={toggle}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50",
        isPrivate &&
          "border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-800 dark:border-white dark:bg-white dark:text-neutral-900",
      )}
      title={
        isPrivate
          ? "Solo lo veis los administradores. Pulsa para que lo vean también los setters"
          : "Esconder este chat a los setters (empresa, socios, instaladores…)"
      }
    >
      <Icon className="h-3.5 w-3.5" />
      Solo admins
    </button>
  );
};
